// ignoring this for the entire file because dnd requires lots of props to be spread
import styled from '@emotion/styled';
import type { DropResult } from '@hello-pangea/dnd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useCallback } from 'react';
import ReactDOM from 'react-dom';
import type { FieldErrors } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileSkill, SkillProficiencyLevel } from '~/shared/data-contract/skill';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { IconDelete } from '~/ui/core/icons';

import { useActiveLanguage } from '../../providers/ActiveLanguageProvider';
import type { CompetencyWithSkills, ProfileForm } from '../types';
import { SkillList } from './SkillList';
import { LANGUAGE_SKILLS_ID, WITHOUT_COMPETENCY_ID } from './use-skills-with-compentencies';

const SkillCategoryContainer = styled.div(({ theme }) => ({
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
    ...theme.typography.body.S.medium,
    width: '100%',
}));

const CategoryTitle = styled.div(({ theme }) => ({
    alignItems: 'center',
    color: 'var(--color-text-primary)',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    justifyItems: 'space-between',
    overflow: 'hidden',
    position: 'relative',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    textWrap: 'wrap',
    ...theme.typography.body.S.medium,
    whiteSpace: 'wrap',
    width: '100%',
}));

const DragWrapper = styled.div<{ readonly isDragEnabled: boolean }>(({ isDragEnabled }) => ({
    '& > *': {
        cursor: isDragEnabled ? 'grab' : 'default',
    },
    '&:focus-visible': {
        outline: 'none',
    },
    cursor: isDragEnabled ? 'grab' : 'default',
    width: '100%',
}));

const DroppableWrapper = styled.div(() => ({
    width: '100%',
}));

const PlaceholderText = styled.div({
    color: 'var(--color-text-content-subtle)',
    userSelect: 'none',
});

const isSystemCategory = (categoryId: string): boolean =>
    categoryId === WITHOUT_COMPETENCY_ID || categoryId === LANGUAGE_SKILLS_ID;

const rendersNothing = (category: CompetencyWithSkills, isEditMode: boolean): boolean =>
    category.title === '' && category.skills.length === 0 && (!isEditMode || isSystemCategory(category.id));

type SkillCategoryProps = {
    readonly category: CompetencyWithSkills;
    readonly commentable: boolean;
    readonly formErrors?: FieldErrors<ProfileForm>;
    readonly highlightSelectedSkill?: boolean;
    readonly isEditMode: boolean;
    readonly itemId?: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onOpenSkillDetails: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly onRemoveCompetency: (competencyId: string) => void;
    readonly onRemoveSkill: (competencyId: string, skillId: string) => void;
    readonly onSkillChange?: () => void;
    readonly showProficiency: boolean;
};

export const SkillCategory: FunctionComponent<SkillCategoryProps> = ({
    category,
    commentable,
    formErrors,
    highlightSelectedSkill = false,
    isEditMode,
    itemId,
    newThreadCreationDisabled = false,
    onChangeProficiencyLevel,
    onOpenSkillDetails,
    onRemoveCompetency,
    onRemoveSkill,
    onSkillChange,
    showProficiency,
}) => {
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const isSystemSkillCategory = isSystemCategory(category.id);
    const categoryTitle =
        category.id === LANGUAGE_SKILLS_ID
            ? translate('common.enums.skillType.language')
            : (category.titles.find((title) => title.language === activeLanguage)?.title ?? category.title);

    const removeSkill = useCallback(
        (skillId: string) => {
            onRemoveSkill(category.id, skillId);
        },
        [category.id, onRemoveSkill],
    );

    const skills = (
        <SkillList
            commentable={commentable}
            formErrors={formErrors}
            highlightSelectedSkill={highlightSelectedSkill}
            isInEditMode={isEditMode}
            itemId={itemId}
            newThreadCreationDisabled={newThreadCreationDisabled}
            openSkillDetails={onOpenSkillDetails}
            showProficiency={showProficiency}
            skills={category.skills}
            onChangeProficiencyLevel={onChangeProficiencyLevel}
            onRemoveSkill={removeSkill}
            onSkillChange={onSkillChange}
        />
    );

    return (
        <SkillCategoryContainer>
            {category.title !== '' && (
                <CategoryTitle>
                    {categoryTitle}
                    {!is.undefined(category.diff) ? (
                        <Badge variant={category.diff === 'added' ? 'success' : 'error'}>
                            {translate(`components.skills.${category.diff}`)}
                        </Badge>
                    ) : null}
                    {isEditMode && !isSystemSkillCategory && (
                        <Button
                            aria-label={translate('common.delete-named', { name: categoryTitle })}
                            data-testid="delete-category-button"
                            iconOnly={true}
                            size="sm"
                            variant="tertiary"
                            onClick={() => onRemoveCompetency(category.id)}
                        >
                            <IconDelete size={16} />
                        </Button>
                    )}
                </CategoryTitle>
            )}
            {isEditMode ? (
                <Droppable droppableId={category.id} isDropDisabled={category.id === LANGUAGE_SKILLS_ID} type="skill">
                    {({ droppableProps, innerRef, placeholder }) => (
                        <DroppableWrapper ref={innerRef} {...droppableProps}>
                            {category.skills.length === 0 && isEditMode && !isSystemSkillCategory && (
                                <PlaceholderText>
                                    {translate('pages.job-architecture.job-profile-empty-competency-placeholder')}
                                </PlaceholderText>
                            )}
                            {skills}
                            {placeholder}
                        </DroppableWrapper>
                    )}
                </Droppable>
            ) : (
                <DroppableWrapper>{skills}</DroppableWrapper>
            )}
        </SkillCategoryContainer>
    );
};

export const ReadOnlySkillCategory: FunctionComponent<{
    readonly children: ReactNode;
    readonly title?: string;
}> = ({ children, title }) => (
    <div className="w-full" style={{ marginBottom: '1rem' }}>
        <SkillCategoryContainer>
            {is.nonEmptyString(title) ? <CategoryTitle>{title}</CategoryTitle> : null}
            <div className="w-full">{children}</div>
        </SkillCategoryContainer>
    </div>
);

export const CategoryList: FunctionComponent<{
    readonly commentable: boolean;
    readonly competenciesWithSkills: readonly CompetencyWithSkills[];
    readonly formErrors?: FieldErrors<ProfileForm>;
    readonly handleOnDragEnd: (result: DropResult<string>) => void;
    readonly highlightSelectedSkill?: boolean;
    readonly isInEditMode: boolean;
    readonly itemId?: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onOpenSkillDetails: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly onRemoveCompetency: (competencyId: string) => void;
    readonly onRemoveSkill: (competencyId: string, skillId: string) => void;
    readonly onSkillChange?: () => void;
    readonly showProficiency: boolean;
}> = ({
    commentable,
    competenciesWithSkills,
    formErrors,
    handleOnDragEnd,
    highlightSelectedSkill = false,
    isInEditMode,
    itemId,
    newThreadCreationDisabled = false,
    onChangeProficiencyLevel,
    onOpenSkillDetails,
    onRemoveCompetency,
    onRemoveSkill,
    onSkillChange,
    showProficiency,
}) => {
    if (!isInEditMode) {
        return (
            <div>
                {competenciesWithSkills.map((competency) => (
                    <DragWrapper
                        key={competency.id}
                        className={rendersNothing(competency, false) ? undefined : 'mb-xxl'}
                        isDragEnabled={false}
                    >
                        <SkillCategory
                            category={competency}
                            commentable={commentable}
                            formErrors={formErrors}
                            highlightSelectedSkill={highlightSelectedSkill}
                            isEditMode={false}
                            itemId={itemId}
                            newThreadCreationDisabled={newThreadCreationDisabled}
                            showProficiency={showProficiency}
                            onChangeProficiencyLevel={onChangeProficiencyLevel}
                            onOpenSkillDetails={onOpenSkillDetails}
                            onRemoveCompetency={onRemoveCompetency}
                            onRemoveSkill={onRemoveSkill}
                            onSkillChange={onSkillChange}
                        />
                    </DragWrapper>
                ))}
            </div>
        );
    }

    return (
        <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="competencies" type="competency">
                {({ droppableProps, innerRef, placeholder }) => (
                    <div ref={innerRef} {...droppableProps}>
                        {competenciesWithSkills.map((competency, index) => (
                            <Draggable
                                key={competency.id}
                                draggableId={competency.id}
                                index={index}
                                isDragDisabled={isSystemCategory(competency.id)}
                            >
                                {(
                                    { innerRef, dragHandleProps, draggableProps: { style, ...draggableProps } },
                                    snapshot,
                                ) => {
                                    const spacingClassName = rendersNothing(competency, isInEditMode)
                                        ? undefined
                                        : 'mb-xxl';

                                    if (snapshot.isDragging) {
                                        return ReactDOM.createPortal(
                                            <DragWrapper
                                                ref={innerRef}
                                                className={spacingClassName}
                                                isDragEnabled={isInEditMode}
                                                {...dragHandleProps}
                                                {...draggableProps}
                                                style={style}
                                            >
                                                <SkillCategory
                                                    key={competency.id}
                                                    category={competency}
                                                    commentable={commentable}
                                                    formErrors={formErrors}
                                                    highlightSelectedSkill={highlightSelectedSkill}
                                                    isEditMode={isInEditMode}
                                                    itemId={itemId}
                                                    newThreadCreationDisabled={newThreadCreationDisabled}
                                                    showProficiency={showProficiency}
                                                    onChangeProficiencyLevel={onChangeProficiencyLevel}
                                                    onOpenSkillDetails={onOpenSkillDetails}
                                                    onRemoveCompetency={onRemoveCompetency}
                                                    onRemoveSkill={onRemoveSkill}
                                                    onSkillChange={onSkillChange}
                                                />
                                            </DragWrapper>,
                                            document.body,
                                        );
                                    }

                                    return (
                                        <DragWrapper
                                            ref={innerRef}
                                            className={spacingClassName}
                                            isDragEnabled={isInEditMode}
                                            {...dragHandleProps}
                                            {...draggableProps}
                                            style={style}
                                        >
                                            <SkillCategory
                                                key={competency.id}
                                                category={competency}
                                                commentable={commentable}
                                                formErrors={formErrors}
                                                highlightSelectedSkill={highlightSelectedSkill}
                                                isEditMode={isInEditMode}
                                                itemId={itemId}
                                                newThreadCreationDisabled={newThreadCreationDisabled}
                                                showProficiency={showProficiency}
                                                onChangeProficiencyLevel={onChangeProficiencyLevel}
                                                onOpenSkillDetails={onOpenSkillDetails}
                                                onRemoveCompetency={onRemoveCompetency}
                                                onRemoveSkill={onRemoveSkill}
                                                onSkillChange={onSkillChange}
                                            />
                                        </DragWrapper>
                                    );
                                }}
                            </Draggable>
                        ))}
                        {placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};
